import React, { useState, useEffect } from 'react';
import { FileText, Search, Trash2, X, Download, Eye } from 'lucide-react';
import { apiFetch } from '../services/api';

// Ícone que abre um modal com os anexos reais da CPFL pra essa instalação.
// Busca automaticamente assim que a instalação estiver disponível. Usado em
// Editar Cliente e Gerar Cobrança.
export default function AnexosCpflButton({ instalacao }) {
  const [arquivos, setArquivos] = useState(null); // null = ainda não buscou
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [baixando, setBaixando] = useState(null); // nome do arquivo sendo baixado
  const [visualizando, setVisualizando] = useState(null); // nome do arquivo sendo aberto
  const [excluindo, setExcluindo] = useState(null); // nome do arquivo sendo excluído

  useEffect(() => {
    if (instalacao) buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instalacao]);

  const buscar = async () => {
    if (!instalacao) return;
    setBuscando(true);
    setErro(null);
    try {
      const r = await apiFetch(`/api/cpfl/anexos-instalacao/${encodeURIComponent(instalacao)}`);
      setArquivos(r.arquivos || []);
    } catch (err) {
      setErro('Não foi possível buscar os anexos.');
      setArquivos([]);
    } finally {
      setBuscando(false);
    }
  };

  // Busca o arquivo via fetch autenticado (um link comum não manda o token,
  // por isso não pode ser um <a href> direto pra rota) e devolve o blob.
  const buscarBlob = async (arq) => {
    const token = localStorage.getItem('token');
    const url = `/api/cpfl/anexo-arquivo?empresa=${encodeURIComponent(arq.empresa)}&nome=${encodeURIComponent(arq.nome)}`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error('Falha ao buscar o arquivo (status ' + resp.status + ').');
    return resp.blob();
  };

  const baixarArquivo = async (arq) => {
    setBaixando(arq.nome);
    try {
      const blob = await buscarBlob(arq);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = arq.nome;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      alert('Não foi possível baixar o arquivo: ' + err.message);
    } finally {
      setBaixando(null);
    }
  };

  const visualizarArquivo = async (arq) => {
    setVisualizando(arq.nome);
    try {
      const blob = await buscarBlob(arq);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      alert('Não foi possível abrir o arquivo: ' + err.message);
    } finally {
      setVisualizando(null);
    }
  };

  const excluirArquivo = async (arq) => {
    if (!window.confirm(`Remover "${arq.nome}" do sistema?`)) return;
    setExcluindo(arq.nome);
    try {
      const token = localStorage.getItem('token');
      const url = `/api/cpfl/anexo-cache?empresa=${encodeURIComponent(arq.empresa)}&nome=${encodeURIComponent(arq.nome)}`;
      const resp = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Falha ao excluir.');
      setArquivos((prev) => prev.filter((a) => a.nome !== arq.nome));
    } catch (err) {
      alert('Não foi possível excluir: ' + err.message);
    } finally {
      setExcluindo(null);
    }
  };

  if (!instalacao) return null;

  const qtd = arquivos ? arquivos.length : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalAberto(true)}
        title="Anexos do cadastro (CPFL)"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', flex: '0 0 auto',
          padding: '9px 14px', borderRadius: 8,
          border: '1px solid #d1d5db', background: '#fff',
          color: '#374151', cursor: 'pointer', fontWeight: 600, fontSize: 14,
        }}
      >
        <FileText size={16} />
        {buscando ? 'Anexos CPFL...' : `Anexos CPFL${qtd > 0 ? ` (${qtd})` : ''}`}
      </button>

      {modalAberto && (
        <div
          onClick={() => setModalAberto(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 24,
              width: '90%', maxWidth: 640, maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Anexos do cadastro (CPFL)</h3>
              <button onClick={() => setModalAberto(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {buscando && <div style={{ fontSize: 13, color: '#d97706' }}>Buscando anexos...</div>}
            {erro && <div style={{ fontSize: 13, color: '#e11d48' }}>{erro}</div>}
            {!buscando && arquivos !== null && arquivos.length === 0 && !erro && (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>Nenhum arquivo encontrado pra essa instalação.</div>
            )}

            {arquivos !== null && arquivos.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {arquivos.map((arq, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 6,
                      padding: '10px 12px', background: '#f9fafb',
                      border: '1px solid #e5e7eb', borderRadius: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <FileText size={16} style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ fontSize: 12, wordBreak: 'break-all', lineHeight: 1.4 }}>
                        {arq.nome ? arq.nome : '⚠️ SEM NOME'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 24 }}>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{(arq.tamanho / 1024).toFixed(0)} KB</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          type="button"
                          onClick={() => visualizarArquivo(arq)}
                          disabled={visualizando === arq.nome}
                          title="Visualizar"
                          style={{ background: 'none', border: 'none', cursor: visualizando === arq.nome ? 'default' : 'pointer', color: '#059669', padding: 4 }}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => baixarArquivo(arq)}
                          disabled={baixando === arq.nome}
                          title="Baixar"
                          style={{ background: 'none', border: 'none', cursor: baixando === arq.nome ? 'default' : 'pointer', color: '#2563eb', padding: 4 }}
                        >
                          <Download size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => excluirArquivo(arq)}
                          disabled={excluindo === arq.nome}
                          title="Excluir"
                          style={{ background: 'none', border: 'none', cursor: excluindo === arq.nome ? 'default' : 'pointer', color: '#e11d48', padding: 4 }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!buscando && arquivos === null && (
              <button
                type="button"
                onClick={buscar}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 13, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid #d97706', background: '#fffbeb',
                  color: '#d97706', cursor: 'pointer', fontWeight: 600,
                }}
              >
                <Search size={13} /> Buscar anexos
              </button>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                type="button"
                onClick={() => setModalAberto(false)}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: '#4f46e5', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
